import { cn } from '@/lib/utils';
import { Code2, Terminal } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Encadré « Note d'intégration ».
 *
 * Ce composant n'a **aucun équivalent dans un vrai site** : il est là parce
 * que Helvedoc est une démonstration destinée à des développeurs. Chaque
 * écran affiche ainsi, à côté de son rendu, les appels d'API qui le
 * produisent et les règles métier qui s'y appliquent.
 *
 * Pour repartir de ce projet comme base d'un vrai site, il suffit de
 * supprimer les usages de `<DevNote>` : aucune logique applicative n'en
 * dépend.
 */
export function DevNote({
    title,
    endpoints,
    children,
    className,
}: {
    title: string;
    /** Les endpoints appelés par l'écran, ex. `['GET /companies/search']`. */
    endpoints?: string[];
    children: ReactNode;
    className?: string;
}) {
    return (
        <aside
            className={cn(
                'border-brand-100 bg-brand-50/60 rounded-xl border border-dashed px-5 py-4 text-sm',
                className,
            )}
        >
            <p className="text-brand-800 flex items-center gap-2 font-semibold">
                <Code2 className="size-4 shrink-0" aria-hidden />
                {title}
            </p>

            {endpoints && endpoints.length > 0 && (
                <ul className="mt-3 flex flex-wrap gap-1.5">
                    {endpoints.map((endpoint) => (
                        <li
                            key={endpoint}
                            className="border-brand-200 text-brand-700 rounded-md border bg-white px-2 py-1 font-mono text-xs"
                        >
                            {endpoint}
                        </li>
                    ))}
                </ul>
            )}

            <div className="text-brand-900/80 mt-3 space-y-2 leading-relaxed">{children}</div>
        </aside>
    );
}

/**
 * Bloc de code monospace, pour illustrer un extrait d'appel ou de réponse.
 * Le `overflow-x-auto` est indispensable : une ligne de `curl` ne doit jamais
 * faire défiler la page entière horizontalement.
 */
export function CodeBlock({ children, label }: { children: string; label?: string }) {
    return (
        <div className="border-brand-950/40 bg-brand-950 overflow-hidden rounded-lg border">
            {label && (
                <div className="border-brand-900 text-brand-200 flex items-center gap-2 border-b px-3 py-1.5 font-mono text-[11px]">
                    <Terminal className="size-3" aria-hidden />
                    {label}
                </div>
            )}
            <pre className="overflow-x-auto px-3 py-3 font-mono text-xs leading-relaxed text-white/90">
                <code>{children}</code>
            </pre>
        </div>
    );
}
